/**
 * Vinyl theme generation.
 *
 * This is a direct browser port of the `parseFigmaToken.js` CLI script
 * (Documents/parseFigma/parseFigmaToken.js). The pure helpers below are copied
 * verbatim so the generated JSON is byte-for-byte identical to the CLI output.
 * Only the Node I/O (fs read / argv / fs write) is replaced by the calling
 * React screen (file upload / form inputs / GraphQL upload).
 */

const getRGB = (hexColor: string, alphaValue?: number): string => {
  if (hexColor.length === 7) {
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    if (alphaValue !== undefined) return `rgba(${r}, ${g}, ${b}, ${alphaValue})`;
    return `rgb(${r}, ${g}, ${b})`;
  } throw Error('Error: Incorrect hex color format when parsing to RGB!');
};

const parseAlphas = (value: string): number[] => {
  const regexpAlpha = /(\d+[.]?\d*)%/g;
  const matchedAlphas = (value.match(regexpAlpha) ?? []).map((num: string) => {
    const asFraction = Number(num.slice(0, -1)) / 100;
    return Math.round((parseFloat(String(asFraction)) + Number.EPSILON) * 100) / 100;
  });
  return matchedAlphas;
};

const validateColorsAndGradientsSize = (colors: unknown[], gradients: unknown[]): void => {
  if (colors.length !== gradients.length) {
    throw Error('Gradient alpha values length not consistent with colors length!');
  }
};

const parseFigmaGradient = (value: string, useRGB = false, includeAlpha = false): string[] => {
  const regexp = /(#.{6})/g;
  const matchedColors = (value.match(regexp) ?? []).map((item: string) => item.toUpperCase());

  if (matchedColors.length === 0) {
    throw Error('Unable to parse gradient colors!');
  }

  let matchedAlphas: number[] | undefined;
  if (includeAlpha) {
    matchedAlphas = parseAlphas(value);
    validateColorsAndGradientsSize(matchedColors, matchedAlphas);
  }

  const result: string[] = [];
  for (let index = 0; index < matchedColors.length; index++) {
    result.push(
      useRGB ? getRGB(matchedColors[index], matchedAlphas ? matchedAlphas[index] : undefined) : matchedColors[index]
    );
  }
  return result;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FigmaTheme = any;

// Top-level color tokens the theme must expose as `<token>.value`.
const REQUIRED_COLOR_TOKENS = [
  'primary100',
  'primary200',
  'primary300',
  'primary400',
  'primary500',
  'primary600',
  'primary700',
  'primary800',
  'primaryBright',
  'primaryBrighter',
  'chatConversation',
  'secondary200',
  'secondary300',
  'secondary400',
  'secondary500',
  'secondary600',
];

// Gradient tokens the theme must expose as `gradients.<token>.value`.
const REQUIRED_GRADIENT_TOKENS = [
  'gradientPrimary500',
  'gradientPrimaryBackground',
  'gradientPrimaryBright',
  'gradientPrimaryBrighter',
  'gradientPrimaryWhite',
  'gradientAction',
  'gradientScrim',
  'gradientStartNewGame',
  'gradientResults',
  'gradientLevelUp',
  'gradientPlaylistUpgrade',
];

/**
 * Reads a design-token value, supporting both the legacy Tokens Studio format (`value`)
 * and the newer DTCG format (`$value`) used by current Figma token exports.
 */
const tokenValue = (node: unknown): string | undefined => {
  if (!node || typeof node !== 'object') return undefined;
  const token = node as { value?: unknown; $value?: unknown };
  const raw = token.$value ?? token.value;
  return typeof raw === 'string' ? raw : undefined;
};

const hasValue = (node: unknown): boolean => tokenValue(node) !== undefined;

/**
 * Heuristic used to decide whether a top-level key of the figmaToken file is a real theme
 * (vs. Tokens Studio metadata like `$themes` / `$metadata`, or an unrelated token set).
 */
export const looksLikeTheme = (candidate: unknown): boolean => {
  if (!candidate || typeof candidate !== 'object') return false;
  const theme = candidate as Record<string, unknown>;
  return hasValue(theme.primary100) && !!theme.gradients && typeof theme.gradients === 'object';
};

/**
 * Returns a list of missing/invalid token paths for the given theme, so the UI can show a
 * precise message instead of a cryptic "Cannot read properties of undefined" crash.
 */
export const getThemeIssues = (figmaTheme: FigmaTheme): string[] => {
  const issues: string[] = [];
  if (!figmaTheme || typeof figmaTheme !== 'object') {
    return ['the selected theme is not an object'];
  }
  REQUIRED_COLOR_TOKENS.forEach((token) => {
    if (!hasValue(figmaTheme[token])) issues.push(`${token}.value`);
  });
  if (!figmaTheme.gradients || typeof figmaTheme.gradients !== 'object') {
    issues.push('gradients');
  } else {
    REQUIRED_GRADIENT_TOKENS.forEach((token) => {
      if (!hasValue(figmaTheme.gradients[token])) issues.push(`gradients.${token}.value`);
    });
  }
  return issues;
};

const createSP3Theme = (
  figmaTheme: FigmaTheme,
  vinylFolder: string,
  level: number,
  assetBucket: string,
  popupIconUrl?: string
) => {
  // Reads a token value (legacy `value` or DTCG `$value`). Presence is guaranteed by getThemeIssues,
  // which is called before this function; the `?? ''` fallback only keeps the types clean.
  const val = (node: unknown): string => tokenValue(node) ?? '';
  // Base URL for asset references embedded in the theme. Follows the target environment's bucket
  // (defaults to prod `songpop3-assets`, matching the original CLI output byte-for-byte).
  const base = `https://storage.googleapis.com/${assetBucket}/vinyls/${vinylFolder}`;

  const palette: FigmaTheme = {};
  palette.primary = {};
  palette.primary.color100 = val(figmaTheme.primary100).toUpperCase();
  palette.primary.color200 = val(figmaTheme.primary200).toUpperCase();
  palette.primary.color300 = val(figmaTheme.primary300).toUpperCase();
  palette.primary.color400 = val(figmaTheme.primary400).toUpperCase();
  palette.primary.color500 = val(figmaTheme.primary500).toUpperCase();
  palette.primary.color600 = val(figmaTheme.primary600).toUpperCase();
  palette.primary.color700 = val(figmaTheme.primary700).toUpperCase();
  palette.primary.color800 = val(figmaTheme.primary800).toUpperCase();
  palette.primary.colorBright = val(figmaTheme.primaryBright).toUpperCase();
  palette.primary.colorBrighter = val(figmaTheme.primaryBrighter).toUpperCase();
  palette.primary.colorChat = val(figmaTheme.chatConversation).toUpperCase();

  palette.secondary = {};
  palette.secondary.color200 = val(figmaTheme.secondary200).toUpperCase();
  palette.secondary.color300 = val(figmaTheme.secondary300).toUpperCase();
  palette.secondary.color400 = val(figmaTheme.secondary400).toUpperCase();
  palette.secondary.color500 = val(figmaTheme.secondary500).toUpperCase();
  palette.secondary.color600 = val(figmaTheme.secondary600).toUpperCase();

  palette.gradient = {};
  palette.gradient.primary = parseFigmaGradient(val(figmaTheme.gradients.gradientPrimary500), true);
  palette.gradient.primaryBackground = parseFigmaGradient(val(figmaTheme.gradients.gradientPrimaryBackground), true);
  palette.gradient.primaryBright = parseFigmaGradient(val(figmaTheme.gradients.gradientPrimaryBright), true);
  palette.gradient.primaryBrighter = parseFigmaGradient(val(figmaTheme.gradients.gradientPrimaryBrighter), true);
  palette.gradient.primaryWhite = parseFigmaGradient(val(figmaTheme.gradients.gradientPrimaryWhite), true);
  palette.gradient.action = parseFigmaGradient(val(figmaTheme.gradients.gradientAction), true);
  palette.gradient.scrim = parseFigmaGradient(val(figmaTheme.gradients.gradientScrim), true, true);

  const screen: FigmaTheme = {};
  // No uploaded popup icon -> leave it empty (""). We intentionally do NOT fall back to
  // `${base}/char${level}.png` because that file usually doesn't exist, which would be a broken (404) URL.
  screen.popupIcon = popupIconUrl || '';

  screen.Splash = {};
  screen.Splash.backgroundColor = `${base}/screen.Splash.background.png`;
  screen.Splash.animatedOverlay = `${base}/screen.Splash.animatedOverlay.lvl${level}.json`;
  screen.Splash.logo = `${base}/screen.Splash.logo.png`;
  screen.Splash.vinylAssets = {};
  screen.Splash.vinylAssets.hand = `${base}/screen.Splash.vinylAssets.hand.png`;
  screen.Splash.vinylAssets.decorationIcon = `${base}/screen.Splash.vinylAssets.decorationIcon.png`;
  screen.Splash.vinylAssets.backCircle = `${base}/screen.Splash.vinylAssets.backCircle.png`;
  screen.Splash.character = level === 1 ? null : `${base}/screen.Splash.character.lvl${level}.json`;

  screen.Games = {};
  screen.Games.newGameButton = {};
  screen.Games.newGameButton.overlay = `${base}/screen.Games.newGameButton.overlay.png`;
  screen.Games.newGameButton.backgroundColor = parseFigmaGradient(val(figmaTheme.gradients.gradientStartNewGame));

  screen.Result = {};
  screen.Result.topBackgroundColor = parseFigmaGradient(val(figmaTheme.gradients.gradientResults));

  screen.LevelUp = {};
  screen.LevelUp.backgroundColor = parseFigmaGradient(val(figmaTheme.gradients.gradientLevelUp));
  screen.LevelUp.backgroundColorLocations = parseAlphas(val(figmaTheme.gradients.gradientLevelUp));
  validateColorsAndGradientsSize(screen.LevelUp.backgroundColor, screen.LevelUp.backgroundColorLocations);

  screen.PlaylistUpgrade = {};
  screen.PlaylistUpgrade.backgroundColor = parseFigmaGradient(val(figmaTheme.gradients.gradientPlaylistUpgrade));
  screen.PlaylistUpgrade.backgroundColorLocations = parseAlphas(val(figmaTheme.gradients.gradientPlaylistUpgrade));
  validateColorsAndGradientsSize(
    screen.PlaylistUpgrade.backgroundColor,
    screen.PlaylistUpgrade.backgroundColorLocations
  );

  screen.Loading = {};
  screen.Loading.backgroundColor = parseFigmaGradient(val(figmaTheme.gradients.gradientResults));

  return { palette, screen };
};

export interface GeneratedVinylThemeFile {
  filename: string;
  /** Pretty-printed JSON string, identical to what the CLI writes to disk. */
  content: string;
  /** Parsed object, handy for on-screen preview. */
  data: unknown;
}

/**
 * Replaces the CLI's `getFigmaTheme(inputFile, themeName)` + the 3-level write loop.
 * Given the parsed figmaToken.json object, produces the 3 theme files
 * (`{themeName}ThemeLvl{1,2,3}.json`).
 *
 * `assetBucket` sets the bucket used in the embedded asset URLs. It defaults to the prod
 * `songpop3-assets` (byte-for-byte identical to the original CLI); pass the current
 * environment's bucket (e.g. `songpop3-test-assets`) so QA in test resolves against test assets.
 */
export const generateVinylThemes = (
  figmaToken: Record<string, unknown>,
  themeName: string,
  vinylFolder: string,
  assetBucket = 'songpop3-assets',
  popupIconUrls: Record<number, string | null | undefined> = {}
): GeneratedVinylThemeFile[] => {
  const figmaTheme = figmaToken[themeName];
  if (!figmaTheme) {
    throw new Error(`Theme "${themeName}" not found inside the figmaToken file.`);
  }

  const issues = getThemeIssues(figmaTheme);
  if (issues.length > 0) {
    throw new Error(
      `Theme "${themeName}" is missing ${issues.length} required token(s): ${issues.join(', ')}. ` +
        `Make sure you selected the right theme and that the figmaToken export is complete.`
    );
  }

  return [1, 2, 3].map((level) => {
    const resultJson = createSP3Theme(figmaTheme, vinylFolder, level, assetBucket, popupIconUrls[level] || undefined);
    return {
      filename: `${themeName}ThemeLvl${level}.json`,
      content: JSON.stringify(resultJson, null, 2),
      data: resultJson,
    };
  });
};

/**
 * Fetch a stored vinyl file, bypassing both the browser cache and GCS edge cache. Bucket objects are
 * served with `Cache-Control: public, max-age=3600`, so a plain fetch keeps returning the previous
 * theme JSON for up to an hour after it is re-uploaded.
 */
export const fetchFresh = (url: string, init?: RequestInit): Promise<Response> =>
  fetch(`${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`, { ...init, cache: 'no-store' });
